import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, ArrowDown, ArrowUp, ChevronRight, FileText, Image as ImageIcon,
  Layers, Loader2, Pencil, Plus, RotateCcw, Save, Search, Trash2, X,
} from 'lucide-react';
import { adminApi } from '../services/api';
import {
  annoncerErreur, annoncerSucces, EtatErreur, EtatSansDonnees, Squelette as Os, validerFichier,
} from '../etats';

// Ce que le champ image annonce (« PNG, JPG ou SVG ») : vérifié avant l'envoi.
const EXTENSIONS_IMAGE = ['.png', '.jpg', '.jpeg', '.svg', '.webp', '.gif'];

/* ═══════════════════════════════════════════════════════════════════════════
   GESTION DES CONTENUS

   L'interface ne connait AUCUN modele du serveur. Elle demande le schema, puis
   construit ses formulaires a partir de lui. Ajouter un champ cote serveur
   suffit donc a le voir apparaitre ici, correctement etiquete et avec le bon
   type de saisie — sans toucher a ce fichier.
   ═══════════════════════════════════════════════════════════════════════════ */

/* Le back-office pilote ses couleurs par variables CSS (AdminLayout), avec deux
   themes : « light » et « forest ». Cette page les respecte desormais au lieu
   de figer ses teintes — c'est ce qui la rendait illisible en thème sombre, et
   ce qui donnait un violet hors palette sur l'entree active.

   `ENCRE_SUR_ACCENT` est le bleu nuit pose sur l'accent vert, exactement comme
   le fait le menu principal : le vert est trop clair pour porter du texte
   blanc (contraste 2,4:1), il lui faut une encre sombre. */
const ENCRE_SUR_ACCENT = '#0A1128';

/* ─────────────────────────── Briques d'interface ─────────────────────────── */

function Etiquette({ children }) {
  return (
    <span className="sous-titre-page">
      {children}
    </span>
  );
}

function Bouton({ variante = 'principal', enCours, icone: Icone, children, ...props }) {
  // Ce composant avait ses propres mesures -- rayon de 6 px, hauteur posee
  // en style en ligne, quatre jeux de couleurs ecrits a la main. Il est
  // desormais adosse a la charte : un bouton du CMS a exactement la forme
  // d'un bouton d'ailleurs, et suivra ses evolutions sans qu'on y revienne.
  const variantes = {
    principal: '',
    secondaire: 'act-contour',
    danger: 'act-danger',
    discret: 'act-tertiaire',
  };
  return (
    <button
      type="button"
      className={`act ${variantes[variante]}`}
      disabled={enCours || props.disabled}
      aria-busy={enCours || undefined}
      {...props}
    >
      {enCours ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
               : Icone && <Icone className="w-3.5 h-3.5" aria-hidden="true" />}
      {children}
    </button>
  );
}

/* Les retours d'action passent par les toasts du système d'états : une
   réussite s'efface seule, une erreur reste jusqu'à ce qu'on la ferme. */
const signaler = (ton, message) => {
  if (!message) return;
  if (ton === 'succes') annoncerSucces(message);
  else annoncerErreur(message);
};

/** La forme d'un formulaire : une étiquette, un champ, autant de fois. */
function Squelette({ lignes = 4 }) {
  return (
    <div className="space-y-4" role="status" aria-label="Chargement">
      {Array.from({ length: lignes }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Os className="h-2 w-24" />
          <Os className="h-11 !rounded-pastille" />
        </div>
      ))}
    </div>
  );
}

/* ──────────────────────────── Champ generique ────────────────────────────── */

// Le champ de la charte : 44 px, filet de 1,5 px, rayon de 14 px, et un
// anneau vert au focus. Les valeurs vivent dans la feuille, pas ici.
const CLASSE_SAISIE = 'champ';

function ChampDynamique({ champ, valeur, onChange, onFichier, erreur: erreurServeur }) {
  const id = `cms-${champ.nom}`;
  // Un fichier refusé avant l'envoi : dit sous le champ, comme une erreur serveur.
  const [refus, setRefus] = useState('');
  const erreur = refus || erreurServeur;
  const commun = { id, 'aria-describedby': champ.aide ? `${id}-aide` : undefined };

  const rendu = () => {
    switch (champ.type) {
      case 'booleen':
        return (
          <button
            type="button"
            role="switch"
            aria-checked={Boolean(valeur)}
            onClick={() => onChange(!valeur)}
            className={`relative inline-flex h-7 w-12 shrink-0 rounded-controle transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] ${valeur ? '' : 'bg-[var(--border)]'}`}
            style={valeur ? { backgroundColor: 'var(--accent)' } : undefined}
            {...commun}
          >
            <span className={`absolute top-1 h-5 w-5 rounded-controle bg-white shadow transition-all ${valeur ? 'left-6' : 'left-1'}`} />
          </button>
        );

      case 'choix':
        return (
          <select {...commun} className={CLASSE_SAISIE} value={valeur ?? ''}
                  onChange={(e) => onChange(e.target.value)}>
            {!champ.obligatoire && <option value="">—</option>}
            {champ.choix.map((c) => (
              <option key={c.valeur} value={c.valeur}>{c.libelle}</option>
            ))}
          </select>
        );

      case 'texte_long':
      case 'json':
        return (
          <textarea {...commun} className={`${CLASSE_SAISIE} min-h-[130px] leading-relaxed resize-y`}
                    value={valeur ?? ''} onChange={(e) => onChange(e.target.value)} />
        );

      case 'image':
        return (
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-pastille border border-[var(--border)] bg-[var(--bg-main)] flex items-center justify-center overflow-hidden shrink-0">
              {valeur
                ? <img src={valeur} alt="" className="w-full h-full object-contain" />
                : <ImageIcon className="w-6 h-6 text-[var(--text-muted)]" aria-hidden="true" />}
            </div>
            <div className="flex-1 min-w-0">
              <input
                {...commun}
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  const motif = f ? validerFichier(f, { extensions: EXTENSIONS_IMAGE }) : null;
                  setRefus(motif || '');
                  if (motif) { e.target.value = ''; onFichier(champ.nom, null); return; }
                  onFichier(champ.nom, f);
                }}
                /* Le bouton natif portait `file:text-white` sans aucun fond :
                   texte blanc sur fond blanc, donc invisible. Il reprend ici
                   l'accent de l'application, avec l'encre sombre qui va avec. */
                className="block w-full text-[12px] text-[var(--text-muted)] file:mr-3 file:min-h-[40px] file:px-4 file:rounded-controle file:border-0 file:text-[12px] file:font-semibold file:cursor-pointer file:bg-[var(--accent)] file:text-[#0A1128]"
              />
              <p className="mt-1.5 text-[10px] text-[var(--text-muted)]">PNG, JPG ou SVG. Remplace l'image actuelle.</p>
            </div>
          </div>
        );

      case 'entier':
        return (
          <input {...commun} type="number" className={CLASSE_SAISIE} value={valeur ?? ''}
                 onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
        );

      case 'email':
      case 'url':
      case 'date':
        return (
          <input {...commun} type={champ.type === 'date' ? 'date' : champ.type}
                 className={CLASSE_SAISIE} value={valeur ?? ''}
                 onChange={(e) => onChange(e.target.value)} />
        );

      default:
        return (
          <input {...commun} type="text" className={CLASSE_SAISIE} value={valeur ?? ''}
                 maxLength={champ.longueur_max || undefined}
                 onChange={(e) => onChange(e.target.value)} />
        );
    }
  };

  const compteur = champ.longueur_max && ['texte', 'texte_long'].includes(champ.type)
    ? `${String(valeur ?? '').length}/${champ.longueur_max}` : null;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2 gap-3">
        <label htmlFor={id} className="text-[13px] font-medium text-[var(--text-main)]">
          {champ.libelle}
          {champ.obligatoire && <span className="text-[var(--alerte)] ml-1" aria-hidden="true">*</span>}
        </label>
        {compteur && <span className="text-[9px] font-bold text-[var(--text-muted)] tabular-nums">{compteur}</span>}
      </div>
      {rendu()}
      {champ.aide && (
        <p id={`${id}-aide`} className="mt-1.5 text-[10px] text-[var(--text-muted)] leading-relaxed">{champ.aide}</p>
      )}
      {erreur && <p className="mt-1.5 text-[10px] font-bold text-[var(--alerte)]">{erreur}</p>}
    </div>
  );
}

/* ─────────────────────── Formulaire (section unique) ─────────────────────── */

function FormulaireSection({ section, onMessage }) {
  const [donnees, setDonnees] = useState(null);
  const [initiales, setInitiales] = useState(null);
  const [fichiers, setFichiers] = useState({});
  const [erreurs, setErreurs] = useState({});
  const [chargement, setChargement] = useState(true);
  const [erreurChargement, setErreurChargement] = useState(null);
  const [essai, setEssai] = useState(0);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    let actif = true;
    setChargement(true);
    setErreurChargement(null);
    setErreurs({});
    setFichiers({});
    adminApi.cmsGetSection(section.cle)
      .then((r) => {
        if (!actif) return;
        setDonnees(r.donnees);
        setInitiales(r.donnees);
      })
      // L'objet entier : son statut dit lequel des cas afficher.
      .catch((e) => actif && setErreurChargement(e))
      .finally(() => actif && setChargement(false));
    return () => { actif = false; };
  }, [section.cle, essai]);

  const modifie = useMemo(
    () => JSON.stringify(donnees) !== JSON.stringify(initiales) || Object.keys(fichiers).length > 0,
    [donnees, initiales, fichiers],
  );

  const enregistrer = async () => {
    setEnvoi(true);
    setErreurs({});
    try {
      let resultat;
      if (Object.keys(fichiers).length > 0) {
        const fd = new FormData();
        section.champs.forEach((c) => {
          if (c.type === 'image') return;                     // gere separement
          const v = donnees[c.nom];
          if (v !== null && v !== undefined) fd.append(c.nom, v);
        });
        Object.entries(fichiers).forEach(([nom, f]) => f && fd.append(nom, f));
        resultat = await adminApi.cmsSaveSectionFichiers(section.cle, fd);
      } else {
        const utiles = {};
        section.champs.forEach((c) => {
          if (c.type === 'image') return;
          utiles[c.nom] = donnees[c.nom];
        });
        resultat = await adminApi.cmsSaveSection(section.cle, utiles);
      }
      setDonnees(resultat);
      setInitiales(resultat);
      setFichiers({});
      onMessage('succes', `« ${section.libelle} » enregistre. Les visiteurs voient la nouvelle version.`);
    } catch (e) {
      const parChamp = e.details && typeof e.details === 'object' ? e.details : {};
      const champsConnus = new Set(section.champs.map((c) => c.nom));
      const cibles = Object.fromEntries(
        Object.entries(parChamp).filter(([nom]) => champsConnus.has(nom)),
      );
      setErreurs(cibles);
      onMessage(
        'erreur',
        Object.keys(cibles).length
          ? 'Certains champs doivent etre corriges avant l enregistrement.'
          : e.message,
      );
    } finally {
      setEnvoi(false);
    }
  };

  if (chargement) return <Squelette lignes={5} />;
  if (erreurChargement) return <EtatErreur erreur={erreurChargement} onReessayer={() => setEssai((n) => n + 1)} compact />;
  if (!donnees) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
        {section.champs.map((champ) => (
          <div key={champ.nom} className={['texte_long', 'json', 'image'].includes(champ.type) ? 'lg:col-span-2' : ''}>
            <ChampDynamique
              champ={champ}
              valeur={donnees[champ.nom]}
              erreur={erreurs[champ.nom]?.[0]}
              onChange={(v) => setDonnees((d) => ({ ...d, [champ.nom]: v }))}
              onFichier={(nom, f) => setFichiers((x) => ({ ...x, [nom]: f }))}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-4 pt-5 border-t border-[var(--border)] sticky bottom-0 bg-[var(--bg-card)]/95 backdrop-blur py-4 -mx-6 px-6 flex-wrap">
        <p className="text-[10px] font-bold text-[var(--text-muted)]">
          {modifie ? 'Modifications non enregistrees' : 'Tout est a jour'}
        </p>
        <div className="flex items-center gap-2">
          {modifie && (
            <Bouton variante="secondaire" icone={RotateCcw}
                    onClick={() => { setDonnees(initiales); setFichiers({}); }}>
              Annuler
            </Bouton>
          )}
          <Bouton icone={Save} enCours={envoi} disabled={!modifie} onClick={enregistrer}>
            Enregistrer
          </Bouton>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Collection d'elements ─────────────────────────── */

function ResumeElement({ element, champs }) {
  const titre = champs.find((c) => ['texte'].includes(c.type) && element[c.nom]);
  const image = champs.find((c) => c.type === 'image' && element[c.nom]);
  const secondaire = champs.filter((c) => c !== titre && ['texte', 'texte_long'].includes(c.type) && element[c.nom])[0];
  return (
    <div className="flex items-center gap-3 min-w-0">
      {image && (
        <img src={element[image.nom]} alt="" className="w-10 h-10 rounded-pastille object-contain bg-[var(--bg-main)] border border-[var(--border)] shrink-0" />
      )}
      <div className="min-w-0">
        <p className="text-[13px] font-semibold text-[var(--text-bold)] truncate">
          {titre ? element[titre.nom] : `Element ${element.id}`}
        </p>
        {secondaire && (
          <p className="text-[11px] text-[var(--text-muted)] truncate">{String(element[secondaire.nom]).slice(0, 90)}</p>
        )}
      </div>
    </div>
  );
}

function CollectionSection({ section, onMessage }) {
  const [elements, setElements] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [edite, setEdite] = useState(null);     // element en cours d'edition, ou 'nouveau'
  const [brouillon, setBrouillon] = useState({});
  const [fichiers, setFichiers] = useState({});
  const [erreurs, setErreurs] = useState({});
  const [envoi, setEnvoi] = useState(false);
  const [suppression, setSuppression] = useState(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const [erreurChargement, setErreurChargement] = useState(null);

  // `silencieux` : rechargement après une action, la liste reste affichée.
  const charger = useCallback(({ silencieux = false } = {}) => {
    if (!silencieux) setChargement(true);
    return adminApi.cmsGetCollection(section.cle)
      .then((r) => { setElements(r.elements); setErreurChargement(null); })
      .catch((e) => (silencieux ? onMessage('erreur', e.message) : setErreurChargement(e)))
      .finally(() => setChargement(false));
  }, [section.cle, onMessage]);

  useEffect(() => { charger(); setEdite(null); }, [charger]);

  const ouvrir = (element) => {
    setEdite(element || 'nouveau');
    setFichiers({});
    setErreurs({});
    if (element) {
      setBrouillon({ ...element });
    } else {
      // Les valeurs initiales viennent des defauts declares sur le modele.
      // Partir d'une chaine vide pour tout faisait echouer la creation en 400
      // des le premier essai : « » n'est pas une icone valide.
      const vide = {};
      section.champs.forEach((c) => { vide[c.nom] = c.defaut ?? ''; });
      setBrouillon(vide);
    }
  };

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      const avecFichiers = Object.keys(fichiers).length > 0;
      let payload;
      if (avecFichiers) {
        payload = new FormData();
        section.champs.forEach((c) => {
          if (c.type === 'image') return;
          const v = brouillon[c.nom];
          if (v !== null && v !== undefined && v !== '') payload.append(c.nom, v);
        });
        Object.entries(fichiers).forEach(([nom, f]) => f && payload.append(nom, f));
      } else {
        payload = {};
        section.champs.forEach((c) => {
          if (c.type === 'image') return;
          payload[c.nom] = brouillon[c.nom];
        });
      }

      if (edite === 'nouveau') {
        await (avecFichiers
          ? adminApi.cmsCreerElementFichiers(section.cle, payload)
          : adminApi.cmsCreerElement(section.cle, payload));
        onMessage('succes', 'Element ajoute.');
      } else {
        await (avecFichiers
          ? adminApi.cmsModifierElementFichiers(section.cle, edite.id, payload)
          : adminApi.cmsModifierElement(section.cle, edite.id, payload));
        onMessage('succes', 'Element mis a jour.');
      }
      setEdite(null);
      setErreurs({});
      await charger({ silencieux: true });
    } catch (e) {
      // Une 400 de DRF designe les champs fautifs : on les affiche sous chacun
      // plutot que de renvoyer l'utilisateur a un message global sans piste.
      const parChamp = e.details && typeof e.details === 'object' ? e.details : {};
      const champsConnus = new Set(section.champs.map((c) => c.nom));
      const cibles = Object.fromEntries(
        Object.entries(parChamp).filter(([nom]) => champsConnus.has(nom)),
      );
      setErreurs(cibles);
      onMessage(
        'erreur',
        Object.keys(cibles).length
          ? 'Certains champs doivent etre corriges avant l enregistrement.'
          : e.message,
      );
    } finally {
      setEnvoi(false);
    }
  };

  const supprimer = async (element) => {
    setSuppressionEnCours(true);
    try {
      await adminApi.cmsSupprimerElement(section.cle, element.id);
      setSuppression(null);
      onMessage('succes', 'Element supprime.');
      await charger({ silencieux: true });
    } catch (e) {
      onMessage('erreur', e.message);
    } finally {
      setSuppressionEnCours(false);
    }
  };

  const deplacer = async (index, direction) => {
    const cible = index + direction;
    if (cible < 0 || cible >= elements.length) return;
    const copie = [...elements];
    [copie[index], copie[cible]] = [copie[cible], copie[index]];
    setElements(copie);                                   // retour visuel immediat
    try {
      await adminApi.cmsReordonner(section.cle, copie.map((e) => e.id));
    } catch (e) {
      onMessage('erreur', e.message);
      charger({ silencieux: true });                      // l'ordre serveur fait foi
    }
  };

  if (chargement) return <Squelette lignes={3} />;
  if (erreurChargement) return <EtatErreur erreur={erreurChargement} onReessayer={() => charger()} compact />;

  return (
    <div className="space-y-4">
      {elements.length === 0 && !edite && (
        <div className="text-center py-14 px-6 rounded-pastille border-2 border-dashed border-[var(--border)]">
          <div className="w-12 h-12 rounded-pastille bg-[var(--bg-main)] flex items-center justify-center mx-auto mb-4">
            <Layers className="w-5 h-5 text-[var(--text-muted)]" aria-hidden="true" />
          </div>
          <p className="text-sm font-semibold text-[var(--text-main)] mb-1">Aucun element pour le moment</p>
          <p className="text-[12px] text-[var(--text-muted)] mb-5 max-w-sm mx-auto leading-relaxed">
            {section.description}
          </p>
          <Bouton icone={Plus} onClick={() => ouvrir(null)}>Ajouter le premier</Bouton>
        </div>
      )}

      {elements.map((element, index) => (
        <div key={element.id}
             className="flex items-center gap-3 p-4 rounded-carte shadow-carte bg-[var(--bg-card)] hover:border-[var(--accent)] transition-colors">
          {section.ordonnable && (
            <div className="flex flex-col gap-0.5 shrink-0">
              <button type="button" onClick={() => deplacer(index, -1)} disabled={index === 0}
                      aria-label={`Monter « ${element.label || element.title || element.name || element.id} »`}
                      className="act act-icone act-tertiaire">
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => deplacer(index, 1)} disabled={index === elements.length - 1}
                      aria-label={`Descendre « ${element.label || element.title || element.name || element.id} »`}
                      className="act act-icone act-tertiaire">
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <div className="flex-1 min-w-0">
            <ResumeElement element={element} champs={section.champs} />
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <button type="button" onClick={() => ouvrir(element)} aria-label="Modifier"
                    className="act act-icone act-tertiaire">
              <Pencil className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => setSuppression(element)} aria-label="Supprimer"
                    className="act act-icone act-tertiaire">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}

      {elements.length > 0 && !edite && (
        <Bouton variante="secondaire" icone={Plus} onClick={() => ouvrir(null)}>
          Ajouter un element
        </Bouton>
      )}

      {/* Formulaire d'edition */}
      {edite && (
        <div className="p-6 rounded-pastille border bg-[var(--bg-main)]" style={{ borderColor: 'var(--accent)' }}>
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <h3 className="text-[15px] font-semibold text-[var(--text-bold)]">
              {edite === 'nouveau' ? 'Nouvel element' : 'Modifier'}
            </h3>
            <button type="button" onClick={() => setEdite(null)} aria-label="Fermer le formulaire"
                    className="act act-icone act-tertiaire">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-5">
            {section.champs.map((champ) => (
              <div key={champ.nom} className={['texte_long', 'json', 'image'].includes(champ.type) ? 'lg:col-span-2' : ''}>
                <ChampDynamique
                  champ={champ}
                  valeur={brouillon[champ.nom]}
                  erreur={erreurs[champ.nom]?.[0]}
                  onChange={(v) => setBrouillon((b) => ({ ...b, [champ.nom]: v }))}
                  onFichier={(nom, f) => setFichiers((x) => ({ ...x, [nom]: f }))}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 mt-6 pt-5 border-t border-[var(--border)]">
            <Bouton variante="secondaire" onClick={() => setEdite(null)}>Annuler</Bouton>
            <Bouton icone={Save} enCours={envoi} onClick={enregistrer}>Enregistrer</Bouton>
          </div>
        </div>
      )}

      {/* Confirmation de suppression */}
      {suppression && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
             role="dialog" aria-modal="true" aria-labelledby="titre-suppression">
          <div className="bg-[var(--bg-card)] rounded-carte p-6 max-w-sm w-full shadow-2xl">
            <h3 id="titre-suppression" className="text-base font-semibold text-[var(--text-bold)] mb-2">Supprimer cet element ?</h3>
            <p className="text-[13px] text-[var(--text-muted)] leading-relaxed mb-6">
              Il disparaitra immediatement du site public. Cette action est definitive.
            </p>
            <div className="flex justify-end gap-2">
              <Bouton variante="secondaire" disabled={suppressionEnCours} onClick={() => setSuppression(null)}>Annuler</Bouton>
              <Bouton variante="danger" icone={Trash2} enCours={suppressionEnCours} onClick={() => supprimer(suppression)}>Supprimer</Bouton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────── Page ───────────────────────────────────── */

export default function Cms({ user }) {
  const [sections, setSections] = useState([]);
  const [active, setActive] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreurSchema, setErreurSchema] = useState(null);
  const [essai, setEssai] = useState(0);
  const [recherche, setRecherche] = useState('');

  useEffect(() => {
    setChargement(true);
    setErreurSchema(null);
    adminApi.cmsSchema()
      .then((r) => {
        setSections(r.sections);
        setActive(r.sections[0] || null);
      })
      // L'objet entier : son statut dit lequel des cas afficher.
      .catch((e) => setErreurSchema(e))
      .finally(() => setChargement(false));
  }, [essai]);

  const groupes = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    const filtrees = terme
      ? sections.filter((s) =>
          s.libelle.toLowerCase().includes(terme) || s.description.toLowerCase().includes(terme))
      : sections;
    return filtrees.reduce((acc, s) => {
      (acc[s.groupe] = acc[s.groupe] || []).push(s);
      return acc;
    }, {});
  }, [sections, recherche]);

  // Les roles admis sont ceux que declare la zone 'cms' cote serveur. Ce garde
  // n'est qu'un confort d'affichage — la protection opposable est la
  // permission de l'endpoint, qui lit la meme table.
  if (user && !['SUPER_ADMIN', 'ADMIN'].includes(user.role)) {
    return (
      <div className="p-8">
        <div className="max-w-md mx-auto text-center py-16">
          <div className="w-14 h-14 rounded-pastille bg-[var(--alerte-fond)] flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="w-6 h-6 text-[var(--alerte)]" aria-hidden="true" />
          </div>
          <h2 className="titre-carte text-[var(--text-bold)] mb-2">Acces reserve</h2>
          <p className="text-[13px] text-[var(--text-muted)] leading-relaxed">
            La gestion des contenus modifie ce que voient tous les visiteurs.
            Elle est reservee a l'equipe d'administration.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* En-tete */}
      <header className="mb-6">
        <Etiquette>Contenus du site</Etiquette>
        <h1 className="titre-page mt-1.5">
          Gestion des contenus
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] mt-2 max-w-2xl leading-relaxed">
          Modifiez les textes, images et reglages du site public. Chaque enregistrement
          est visible immediatement par les visiteurs.
        </p>
      </header>

      {chargement ? (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
          <div className="space-y-2" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <Os key={i} className="h-12 !rounded-pastille" />
            ))}
          </div>
          <div className="p-6 rounded-carte shadow-carte bg-[var(--bg-card)]"><Squelette lignes={5} /></div>
        </div>
      ) : erreurSchema ? (
        <div className="rounded-carte shadow-carte bg-[var(--bg-card)]">
          <EtatErreur erreur={erreurSchema} onReessayer={() => setEssai((n) => n + 1)} />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 items-start">
          {/* ── Rail des sections ──────────────────────────────────────────
              C'est cette colonne qui defile. L'inverse etait en place : le
              rail restait fige et le formulaire montait hors de l'ecran, si
              bien qu'on perdait de vue le champ qu'on remplissait et le
              bouton d'enregistrement en cherchant une section plus bas.
              Trente sections tiennent rarement dans une hauteur d'ecran ; un
              formulaire, presque toujours. */}
          <nav aria-label="Sections editables" className="space-y-5">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" aria-hidden="true" />
              <input
                type="search"
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Rechercher une section"
                aria-label="Rechercher une section"
                style={{ minHeight: 44 }}
                className="champ champ-loupe"
              />
            </div>

            {Object.keys(groupes).length === 0 && (
              recherche.trim() ? (
                <EtatSansDonnees cas="sansResultat" compact titre={`Aucune section pour « ${recherche.trim()} »`}
                  action={<button type="button" className="act act-sm act-contour" onClick={() => setRecherche('')}>Effacer la recherche</button>} />
              ) : (
                <EtatSansDonnees cas="vide" compact titre="Aucune section modifiable" aide="Le serveur ne déclare aucun contenu éditable." />
              )
            )}

            {Object.entries(groupes).map(([groupe, liste]) => (
              <div key={groupe}>
                <div className="px-1 mb-2"><Etiquette>{groupe}</Etiquette></div>
                <ul className="space-y-1">
                  {liste.map((s) => {
                    const actif = active?.cle === s.cle;
                    return (
                      <li key={s.cle}>
                        <button
                          type="button"
                          onClick={() => setActive(s)}
                          aria-current={actif ? 'true' : undefined}
                          className={`w-full flex items-center gap-2.5 px-3 py-3 rounded-pastille text-left transition-all ${
                            actif ? 'shadow-sm' : 'text-[var(--text-main)] hover:bg-[var(--bg-main)]'
                          }`}
                          style={{ minHeight: 44, ...(actif ? { backgroundColor: 'var(--accent)', color: ENCRE_SUR_ACCENT } : {}) }}
                        >
                          <span className="text-[13px] font-medium flex-1 min-w-0 truncate">
                            {s.libelle}
                          </span>
                          {s.nature === 'collection' && (
                            <span className={`text-[8px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                              actif ? 'bg-black/10' : 'bg-[var(--bg-main)] text-[var(--text-muted)]'}`}>
                              liste
                            </span>
                          )}
                          <ChevronRight className={`w-3.5 h-3.5 shrink-0 ${actif ? 'opacity-80' : 'opacity-30'}`} aria-hidden="true" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>

          {/* ── Panneau d'edition ──────────────────────────────────────────
              Fixe a l'ecran pendant que le rail defile. Sa hauteur est bornee
              a celle de la fenetre et il defile alors dans ses propres
              bornes : sans ce plafond, un formulaire plus haut que l'ecran
              verrait `sticky` epingler son sommet et rendre son pied — donc
              son bouton d'enregistrement — inatteignable. */}
          <section className="rounded-carte shadow-carte bg-[var(--bg-card)] p-6 min-w-0 lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto"
                   aria-live="polite">
            {active ? (
              <>
                <div className="mb-6 pb-5 border-b border-[var(--border)]">
                  <div className="flex items-center gap-2 mb-1.5">
                    <FileText className="w-4 h-4" style={{ color: 'var(--accent)' }} aria-hidden="true" />
                    <Etiquette>{active.groupe}</Etiquette>
                  </div>
                  <h2 className="titre-carte text-[var(--text-bold)]">{active.libelle}</h2>
                  {active.description && (
                    <p className="text-[12px] text-[var(--text-muted)] mt-1.5 leading-relaxed max-w-2xl">
                      {active.description}
                    </p>
                  )}
                </div>

                {active.nature === 'unique'
                  ? <FormulaireSection section={active} onMessage={signaler} />
                  : <CollectionSection section={active} onMessage={signaler} />}
              </>
            ) : (
              <p className="text-[13px] text-[var(--text-muted)] py-12 text-center">
                Choisissez une section a modifier.
              </p>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
